import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
// icons
import Iconify from 'src/components/Iconify';
import { formataNumeroCelular } from 'src/utils/functions';
// components
import ModalCriarAgente from './ModalCriarAgente';
import ModalConfimacao from 'src/components/modal/ModalConfimacao';
// actions
import v2_actionListaAgentesAi from 'src/actions/v2/ai/v2_actionListaAgentesAi';
import v2_actionDeletarAgente from 'src/actions/v2/ai/v2_actionDeletarAgente';

// ----------------------------------------------------------------------

export default function AgentesIaComponent({ onConfigFlow, user, cliente, onCallback }) {
    const navigate = useNavigate();
    const { t } = useTranslation('settings');
    const { enqueueSnackbar } = useSnackbar();
    const [openModalCriarAgente, setOpenModalCriarAgente] = useState(false);
    const [openModalConfirmacao, setOpenModalConfirmacao] = useState(false);
    const [listaAgentesAi, setListaAgentesAi] = useState([]);
    const [agentId, setAgentId] = useState(null);
    const [loading, setLoading] = useState(true);

    async function buscarListaAgentesAi() {
        try {
            setLoading(true);
            const response = await v2_actionListaAgentesAi(cliente._id);
            if (response.status !== 200) return enqueueSnackbar(t('aiAgents.list.toastLoadListError', 'Erro ao buscar lista de agentes de IA'), { variant: 'error' });
            setListaAgentesAi(response.data.data);
        } catch (error) {
            enqueueSnackbar(t('aiAgents.list.toastLoadError', 'Erro ao buscar agentes de IA'), { variant: 'error' });
            console.log(error);
        } finally {
            setLoading(false);
        }
    }

    // deletar agente
    async function handleDeletarAgente() {
        try {
            const response = await v2_actionDeletarAgente(agentId);
            if (response.status !== 200) return enqueueSnackbar(t('aiAgents.list.toastDeleteError', 'Erro ao deletar agente'), { variant: 'error' });
            enqueueSnackbar(t('aiAgents.list.toastDeleteSuccess', 'Agente deletado com sucesso'), { variant: 'success' });
            buscarListaAgentesAi();
            setOpenModalConfirmacao(false);
            setAgentId(null);
        } catch (error) {
            enqueueSnackbar(t('aiAgents.list.toastDeleteError', 'Erro ao deletar agente'), { variant: 'error' });
            console.log(error);
        }
    }

    useEffect(() => {
        buscarListaAgentesAi();
    }, []);

    return (
        <div className="flex flex-col h-full w-full gap-6 p-2 md:p-0 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <ModalCriarAgente
                cliente={cliente}
                open={openModalCriarAgente}
                onClose={() => setOpenModalCriarAgente(false)}
                onCallback={(agente) => {
                    setOpenModalCriarAgente(false);
                    buscarListaAgentesAi();
                    navigate(`/dashboard/configuracoes/agentOnboarding?id=${agente._id}`);
                }}
            />
            <ModalConfimacao
                open={openModalConfirmacao}
                onClose={() => {
                    setAgentId(null);
                    setOpenModalConfirmacao(false);
                }}
                mensagem={t('aiAgents.list.confirmDeleteMessage', 'Deseja mesmo excluir este agente?')}
                onConfirm={() => handleDeletarAgente()}
            />

            {/* Header - Matches CanaisComponent/Design System */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 tracking-tight">{t('aiAgents.list.title', 'Agentes IA')}</h1>
                    <p className="text-sm text-gray-500 mt-1">{t('aiAgents.list.subtitle', 'Crie e gerencie seus assistentes virtuais inteligentes.')}</p>
                </div>
                {/* Action Button */}
                <button
                    onClick={() => setOpenModalCriarAgente(true)}
                    className="flex items-center gap-2 px-4 py-2.5 bg-[#7C3AED] hover:bg-[#6d28d9] text-white text-sm font-medium rounded-xl transition-all shadow-sm shadow-[#7C3AED]/20 active:transform active:scale-95"
                >
                    <Iconify icon="solar:add-circle-bold" width={20} height={20} />
                    <span>{t('aiAgents.list.newAgentButton', 'Novo Agente')}</span>
                </button>
            </div>

            {/* Content Grid - Matches CanaisComponent grid */}
            <div className="mt-4">
                {loading ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-6">
                        {Array(4)
                            .fill(0)
                            .map((_, index) => (
                                <SkeletonAgentCard key={index} />
                            ))}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-6">
                        {/* Create New Card - Always First */}
                        <CardNew onClick={() => setOpenModalCriarAgente(true)} />
                        {/* Agent List */}
                        {listaAgentesAi &&
                            listaAgentesAi?.map((item, index) => (
                                <AgentAiCard
                                    key={item._id || index}
                                    canal={item}
                                    onClick={() => {
                                        if (item.status_create === 'draft') {
                                            navigate(`/dashboard/configuracoes/agentOnboarding?id=${item._id}`);
                                        } else {
                                            navigate(`/dashboard/configuracoes/agentDetail?id=${item._id}`);
                                        }
                                    }}
                                    onDelete={() => {
                                        setAgentId(item._id);
                                        setOpenModalConfirmacao(true);
                                    }}
                                />
                            ))}
                    </div>
                )}
            </div>
        </div>
    );
}

// ----------------------------------------------------------------------

AgentAiCard.propTypes = {
    canal: PropTypes.object,
    onClick: PropTypes.func,
    onDelete: PropTypes.func,
};

function AgentAiCard({ canal, onClick, onDelete }) {
    const { t } = useTranslation('settings');
    const isDraft = canal.status_create === 'draft';

    return (
        <div
            onClick={onClick}
            className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 relative flex flex-col h-full min-h-[240px] hover:shadow-md hover:border-[#7C3AED]/30 transition-all duration-300 animate-in fade-in slide-in-from-bottom-2 duration-300 cursor-pointer group"
        >
            {/* Header Actions (Status & Delete) - Positioned Absolute Top Right */}
            <div className="absolute top-4 right-4 flex items-center gap-2 z-10">
                {/* Status Dot */}
                <div
                    className={`w-2.5 h-2.5 rounded-full shadow-sm ring-2 ring-white ${!isDraft ? 'bg-green-500' : 'bg-gray-300'
                        }`}
                    title={!isDraft ? t('aiAgents.list.statusActive', 'Ativo') : t('aiAgents.list.statusDraft', 'Rascunho')}
                />
                {/* Delete Button - Visible on Hover */}
                <button
                    className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-all opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto"
                    onClick={(e) => {
                        e.stopPropagation();
                        onDelete();
                    }}
                    title={t('aiAgents.list.deleteAgentTooltip', 'Excluir agente')}
                >
                    <Iconify icon="solar:trash-bin-trash-bold" width={18} height={18} />
                </button>
            </div>

            {/* Content */}
            <div className="flex-1 flex flex-col items-center pt-2">
                {/* Icon with subtle background */}
                <div
                    className={`mb-3 p-3 rounded-lg group-hover:scale-105 transition-transform duration-300 ${isDraft ? 'bg-gray-100 text-gray-500' : 'bg-[#7C3AED]/10 text-[#7C3AED]'
                        }`}
                >
                    <Iconify icon="fluent:bot-sparkle-24-filled" width={28} height={28} />
                </div>
                <h3 className="text-base font-semibold text-gray-900 text-center line-clamp-1 mb-1 px-2" title={canal.name}>
                    {canal.name}
                </h3>
                <p className="text-[11px] text-gray-500 mb-4 bg-gray-50 px-2.5 py-0.5 rounded-full font-medium tracking-wide">
                    {formataNumeroCelular(canal.numero_whatsapp) || t('aiAgents.list.noNumber', 'Sem número')}
                </p>

                {/* Fill space */}
                <div className="flex-1 w-full flex flex-col justify-end gap-3 mt-2">
                    {/* Divider */}
                    <div className="h-px bg-gradient-to-r from-transparent via-gray-100 to-transparent w-full" />
                    {/* Channels Connected Info */}
                    {!isDraft && (
                        <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
                            <Iconify icon="tabler:plug-connected" width={14} />
                            <span>{t('aiAgents.list.channelsConnected', '{{count}} canais conectados', { count: canal?.counts_use || 0 })}</span>
                        </div>
                    )}
                    {isDraft && (
                        <div className="flex items-center justify-center gap-2 text-xs text-amber-600 bg-amber-50 py-1 rounded-md">
                            <Iconify icon="solar:pen-bold" width={12} />
                            <span>{t('aiAgents.list.draftBadge', 'Em rascunho')}</span>
                        </div>
                    )}
                    {/* Config Button Area */}
                    <div className="grid grid-cols-1 w-full">
                        <button className="flex-1 flex items-center justify-center gap-1.5 bg-gray-50 text-gray-700 px-3 py-2 rounded-lg text-xs font-semibold hover:bg-gray-100 hover:text-gray-900 transition-all border border-transparent hover:border-gray-200">
                            <Iconify icon={isDraft ? 'solar:pen-new-square-bold' : 'solar:settings-bold'} width={16} height={16} />
                            <span>{isDraft ? t('aiAgents.list.continueEditButton', 'Continuar Edição') : t('aiAgents.list.configureButton', 'Configurar')}</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ----------------------------------------------------------------------

const CardNew = ({ onClick }) => {
    const { t } = useTranslation('settings');
    return (
        <button
            onClick={onClick}
            className="group relative flex flex-col items-center justify-center p-6 rounded-xl border-2 border-dashed transition-all duration-300 min-h-[240px] w-full animate-in fade-in slide-in-from-bottom-2 duration-300 border-[#7C3AED]/30 bg-[#7C3AED]/5 hover:bg-[#7C3AED]/10 hover:border-[#7C3AED]/50 cursor-pointer"
        >
            <div className="w-14 h-14 rounded-lg flex items-center justify-center mb-3 transition-all duration-300 bg-white text-[#7C3AED] shadow-sm group-hover:scale-110 group-hover:shadow-md">
                <Iconify icon="solar:add-circle-bold" width={32} height={32} />
            </div>
            <h3 className="text-base font-semibold mb-1 text-[#7C3AED]">{t('aiAgents.list.newAgentButton', 'Novo Agente')}</h3>
            <p className="text-sm text-center px-4 text-gray-500 leading-relaxed">{t('aiAgents.list.newAgentDescription', 'Adicione um novo assistente virtual')}</p>
        </button>
    );
};

function SkeletonAgentCard() {
    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 relative overflow-hidden h-[240px]">
            <div className="flex flex-col space-y-6 h-full items-center justify-center">
                <div className="flex flex-col items-center justify-center space-y-4 w-full">
                    <div className="w-12 h-12 bg-gray-100 rounded-lg animate-pulse"></div>
                    <div className="w-32 h-4 bg-gray-100 rounded animate-pulse"></div>
                </div>
                <div className="w-full border-t border-dashed border-gray-100"></div>
                <div className="flex flex-col items-center space-y-3 w-full">
                    <div className="w-24 h-6 bg-gray-100 rounded-lg animate-pulse"></div>
                    <div className="w-full h-8 bg-gray-50 rounded-lg animate-pulse"></div>
                </div>
            </div>
        </div>
    );
}